import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductList, getProductDetails, saveProductBasicInfo } from '../services/productService';
import { getUserInfo } from '../utils/tokenManager';
import ProductTable from '../components/ProductTable';
import ProductSettingsModal from '../components/ProductSettingsModal';
import { getCities, getManagers, getProductTypes } from '../services/apiService';

const SingleProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const reloadList = async () => {
    try {
      setLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) { setError('로그인이 필요합니다.'); return; }
      const list = await getProductList(companyNo, userNo);
      setProducts(Array.isArray(list) ? list : []);
    } catch (e) {
      setError('상품 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadList();
  }, []);

  const openBasicSettings = async (p) => {
    try {
      setSettingsLoading(true);
      const { companyNo } = getUserInfo() || {};
      const details = await getProductDetails(p?.상품번호 || p?.상품코드, companyNo);
      if (details) {
        setSelectedProduct(details);
        setIsSettingsOpen(true);
      } else {
        alert('상품 상세 정보를 찾을 수 없습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('상품 상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsSettingsOpen(false);
    setSelectedProduct(null);
  };

  const handleSaveSettings = async (updatedProduct) => {
    try {
      setSettingsLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      const productNo = updatedProduct.상품번호 || selectedProduct?.상품번호;

      const [cities, managers, productTypes] = await Promise.all([
        getCities().catch(() => []),
        getManagers().catch(() => []),
        getProductTypes().catch(() => []),
      ]);

      const findByName = (list, name) => Array.isArray(list) ? list.find(i => i.코드명 === name) : undefined;
      const original = selectedProduct || {};

      const keywordStr = (updatedProduct.키워드 || '').trim();
      const keywordArr = keywordStr ? keywordStr.split(',').map(k => k.trim()).filter(Boolean) : [];

      const cityName = updatedProduct.도시 ?? original.도시;
      const cityItem = findByName(cities, cityName);
      const cityNo = cityItem?.코드 ?? original.도시번호;

      const productTypeName = updatedProduct.상품타입 ?? original.상품타입;
      const productTypeItem = findByName(productTypes, productTypeName);
      const productTypeCode = productTypeItem?.코드 ?? original.상품타입코드;

      const pdName = updatedProduct.상품담당 ?? original.상품담당;
      const rdName = updatedProduct.예약담당 ?? original.예약담당;
      const pdItem = findByName(managers, pdName);
      const rdItem = findByName(managers, rdName);
      const pdNo = pdItem?.코드 ?? original.상품담당번호;
      const rdNo = rdItem?.코드 ?? original.예약담당번호;

      const basicInfo = {
        ...original,
        상품번호: productNo,
        상품명: updatedProduct.상품명 ?? original.상품명,
        도시번호: cityNo,
        도시: cityName,
        상품타입: productTypeName,
        상품타입코드: productTypeCode,
        상품코드: updatedProduct.상품코드 ?? original.상품코드,
        상품코드룰사용: original.상품코드룰사용 ?? false,
        상품담당: pdName,
        상품담당번호: pdNo,
        예약담당: rdName,
        예약담당번호: rdNo,
        디폴트수배담당: (original.디폴트수배담당 ?? original.예약담당 ?? original.상품담당) || '예약담당',
        소요타입: original.소요타입 ?? original.소요시간타입 ?? original.소요단위 ?? '',
        소요: original.소요 ?? original.소요단위 ?? '',
        소요시간: updatedProduct.소요시간 ?? original.소요시간 ?? 0,
        통화코드: original.통화코드 ?? '',
        통화: original.통화 ?? '',
        가이드타입: original.가이드타입 ?? '',
        가이드타입코드: original.가이드타입코드 ?? '',
        카테고리: original.카테고리 ?? '',
        카테고리코드: original.카테고리코드 ?? '',
        노출여부: typeof updatedProduct.노출여부 === 'boolean' ? updatedProduct.노출여부 : (original.노출여부 ?? false),
        키워드: keywordArr,
        상품뱃지: updatedProduct.상품뱃지 ?? original.상품뱃지,
      };

      await saveProductBasicInfo({ companyNo, userNo, productNo, basicInfo, keywords: keywordArr.join(',') });

      const refreshed = await getProductDetails(productNo, companyNo).catch(() => null);
      if (refreshed) setSelectedProduct(refreshed);
      await reloadList();
      alert('성공적으로 저장되었습니다.');

      return true;
    } catch (e) {
      console.error('Failed to save product details:', e);
      alert('저장에 실패했습니다. 다시 시도해주세요.');

      return false;
    } finally {
      setSettingsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return (products || [])
      .filter(p => String(p?.상품타입 || '') === '단품')
      .filter(p => String(p?.상품종류코드 || '').trim() !== '001001')
      .filter(p => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
          String(p?.상품명 || '').toLowerCase().includes(s) ||
          String(p?.도시 || '').toLowerCase().includes(s) ||
          String(p?.상품코드 || '').toLowerCase().includes(s)
        );
      });
  }, [products, search]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-800">일반상품(단품)</h1>
        <div className="relative w-full max-w-xs">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full pl-3 pr-3 py-2 border border-gray-200 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 text-sm"
            placeholder="상품명/도시/코드 검색..."
          />
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600"></div>
            <p className="text-sm text-gray-500">상품 목록을 불러오는 중...</p>
          </div>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
          <ProductTable
            products={filtered}
            enableColumnFilters
            singleDateFilter
            dateOnly
            disablePagination
            hideLocationTypeColumns
            showNoteBadgeColumns
            showOrdersButton={false}
            onOpenPriceStock={(p) => navigate(`/general/price-stock/${p?.상품번호 || ''}`)}
            onOpenDetailSettings={(p) => navigate(`/general/detail-settings/${p?.상품번호 || ''}`)}
            onOpenSettings={openBasicSettings}
            settingsButtonLabel="기본설정"
            onNameClick={(p) => {
              const productNo = p?.상품번호 || '';
              navigate(`/general/orders/${encodeURIComponent(productNo)}`, { state: { productName: p?.상품명 || '', productNo: p?.상품번호 || null, productCode: p?.상품코드 || null } });
            }}
          />
        </div>
      )}

      {/* 기본설정 모달 */}
      {isSettingsOpen && (
        <ProductSettingsModal
          product={selectedProduct}
          onClose={handleCloseModal}
          onSave={handleSaveSettings}
          loading={settingsLoading}
          hideBasicCity
          hideBasicDuration
          hideDetailTab
        />
      )}
    </div>
  );
};

export default SingleProducts;
